<template>
  <section class="main">
    <html-with-links tag="h1" class="intro" :html="$t('THIS_' + (isMobile ? 'DEVICE' : 'COMPUTER') + '_IS_LINKED_TO_APPDB', {
      email: (devices[0] || {}).email,
      emailChangeLink: '/my/change-email'
    })" />
    <div class="row">
      <div class="devices">
        <div class="device" v-for="device in devices" :class="{
          device_active: device.lt === $store.getters.lt,
          device_disabled: deviceRemoving === device.lt,
          device_switchable: device.lt !== $store.getters.lt && canSwitchToDevice(device) && !transitioning
        }" :key="device.lt" @click="switchDeviceMobile(device.lt, device.link_type, $event)">
          <div class="device__img-wrapper">
            <img class="device__img" :src="device.icon" />
          </div>
          <div class="device__desc">
            <span class="device__title">
              {{ device.model }}
              <span v-if="device.plus" class="device__pro">Plus</span>
              <span v-if="device.premium" class="device__premium">PREMIUM</span>
              <a v-if="device.lt !== $store.getters.lt" ref="removeDevice" href="#" class="device__remove"
                @click.prevent="removeDevice(device.lt)">{{
                  deviceRemoving === device.lt
                    ? $t("REMOVING")
                    : $t("REMOVE")
                }}</a>
            </span>
            <span class="device__info">{{ device.ios }}<span v-if="device.name">, {{ device.name }}</span></span>
          </div>
          <div class="device__switch"
            v-if="device.lt !== $store.getters.lt && canSwitchToDevice(device) && !transitioning">
            <button class="device__button" @click.prevent="switchDevice(device.lt, device.link_type)">
              {{ isSwitchBack(device) ? $t("SWITCH_BACK") : $t("SWITCH") }}
            </button>
            <div class="device__switch-text">{{ $t("TO_THIS_DEVICE") }}</div>
          </div>
          <div class="device__switch"
            v-else-if="device.lt !== $store.getters.lt && !canSwitchToDevice(device) && !transitioning">
            <div class="device__switch-text">{{ $t("CAN_BE_USED_ONLY_DIRECTLY") }}</div>
          </div>
          <div class="device__mobile-indicator">
            <span v-if="device.lt === $store.getters.lt" class="device__mobile-no-switch">
              {{ $t("SELECTED_DEVICE") }}
            </span>
            <span v-else-if="canSwitchToDevice(device) && !transitioning" class="device__mobile-switch">
              {{ $t("SWITCH") }} →
            </span>
            <span v-else-if="!canSwitchToDevice(device) && !transitioning" class="device__mobile-no-switch">
              {{ $t("CAN_BE_USED_ONLY_DIRECTLY") }}
            </span>
            <span v-else-if="transitioning" class="device__mobile-switch">
              {{ $t("SWITCHING") }}...
            </span>
          </div>
        </div>
      </div>
      <div class="link">

        <html-with-links tag="h2" class="link__title" :html="$t('LINK_NEW_APPLE_SILICON_MAC_USE_APPDB_TOOLS', {
          href: '/appdb-tools'
        })" />
        <h2 class="link__title">{{ $t("LINK_NEW_DEVICE") }}</h2>
        <p class="link__text">
          <a href="https://www.youtube.com/watch?v=Tp3chHFX0NE" target="_blank">{{ $t('VIDEO_TUTORIAL')
          }}</a><br /><br />
        </p>
        <p class="link__text">
          {{ $t("IF_YOU_NEED_TO_LINK_NEW_DEVICE_HERES_CODE") }}
        </p>
        <span class="link__code">{{ code }}</span>
        <qrcode class="link__qr" :background="qrCodeBg" :value="`https://a.appdb.to/add/?${code}`" />
        <h2 class="link__title">{{ $t("TO_LINK_APPLE_TV") }}</h2>
        <ul class="link__list" v-html="$t('LINK_APPLE_TV_STEPS', {
          link: `https://a.appdb.to/add/?${code}`,
        })
          "></ul>
      </div>
    </div>
  </section>
</template>

<script>
import isItselfLinkable from '@/utils/isItselfLinkable';
import QrcodeVue from 'qrcode.vue';
import { mapState } from 'vuex';

import HtmlWithLinks from '@/components/HtmlWithLinks.vue';

export default {
  components: { qrcode: QrcodeVue, HtmlWithLinks },
  data() {
    return {
      title: 'My devices',
      code: '',
      isMobile: isItselfLinkable(),
      deviceRemoving: null,
      transitioning: false,
    };
  },
  computed: {
    ...mapState(['theme', 'config']),
    qrCodeBg() {
      // Always return white for QR code background regardless of theme
      return '#FFFFFF';
    },
    devices() {
      return this.$store.getters.devices;
    },
    deviceStr() {
      return isItselfLinkable() ? 'iPhone/iPad/iPod/Vision Pro' : 'PC/Mac';
    },
    currentDevice() {
      return this.devices.find(device => device.lt === this.$store.getters.lt) || {};
    },
  },
  methods: {
    isSwitchBack(device) {
      const oldLinkToken = localStorage.getItem('old_lt');
      // Debug output to check values
      console.log('Switch back check:', {
        currentLinkType: this.config.link_type,
        deviceLinkType: device.link_type,
        oldLinkToken,
        deviceLt: device.lt,
        match: oldLinkToken === device.lt,
      });

      // Handle the case where device.link_type is not available
      const deviceLinkType = device.link_type;

      return this.config.link_type === 'mdm'
        && deviceLinkType === 'profile'
        && oldLinkToken === device.lt;
    },
    canSwitchToDevice(device) {
      if (this.transitioning) {
        return false;
      }

      const oldLinkToken = localStorage.getItem('old_lt');
      const deviceLinkType = device.link_type;

      // Case 1: current is MDM
      if (this.config.link_type === 'mdm') {
        // Can always switch to MDM devices
        if (deviceLinkType === 'mdm') {
          return true;
        }

        // For profile devices, check if it matches the old_lt
        if (deviceLinkType === 'profile') {
          // If old_lt exists but doesn't match any device, clear it
          if (oldLinkToken && !this.devices.some(d => d.lt === oldLinkToken)) {
            localStorage.removeItem('old_lt');
            return false;
          }

          // Can switch to profile device only if it matches old_lt
          return oldLinkToken === device.lt;
        }
      }

      // Case 2: current is profile
      if (this.config.link_type === 'profile') {
        // Can switch to MDM devices
        if (deviceLinkType === 'mdm') {
          return true;
        }

        // For profile devices, check if old_lt doesn't match
        if (deviceLinkType === 'profile') {
          // Cannot switch to other profile devices
          return false;
        }
      }

      // Default fallback
      return true;
    },
    switchDevice(lt, linkType) {
      this.transitioning = true;

      if (this.config.link_type === 'profile') {
        localStorage.setItem('old_lt', this.$store.getters.lt);
      }

      this.$store.commit('lt', lt);
      this.$store.commit('setConfig', {});

      this.$store.dispatch('getConfig')
        .then(() => {
          this.getCode();
        })
        .finally(() => {
          setTimeout(() => {
            this.transitioning = false;
          }, 300);
        });
    },
    switchDeviceMobile(lt, linkType, e) {
      // Check if this is a click on the remove device button
      if (this.$refs.removeDevice && this.$refs.removeDevice.some(el => el === e.target)) {
        return;
      }

      // Get the device by link token
      const device = this.devices.find(d => d.lt === lt);

      // Don't do anything if this is the current device
      if (device.lt === this.$store.getters.lt) {
        return;
      }

      // Check if we can switch to this device
      if (!this.canSwitchToDevice(device)) {
        // Show popup notification if device can't be switched
        if (!device.model.includes('Mac')) {
          this.$notify({
            type: 'error',
            text: this.$t('CAN_BE_USED_ONLY_DIRECTLY_NOTICE'),
          });
        } else {
          this.$notify({
            type: 'error',
            text: this.$t('USE_APPDB_TOOLS_TO_SWITCH_TO_MAC'),
          });
        }
        return;
      }

      // Switch to the device
      this.switchDevice(lt, linkType);
    },
    removeDevice(lt) {
      this.$confirm(this.$t('DEVICE_REMOVAL_WARNING'))
        .then(() => {
          this.deviceRemoving = lt;
        })
        .then(() => this.$api.unlinkDevice(lt))
        .then(() => this.$store.dispatch('getDevices'))
        .finally(() => {
          this.deviceRemoving = null;
        });
    },
    getCode() {
      this.$api.getLinkCode().then((code) => {
        this.code = code;
      });
    },
  },
  created() {
    this.getCode();
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

.main {
  display: block;
  flex-grow: 1;
}

.instruction {
  margin-bottom: 15px;
  font-size: 14px;
}

.row {
  display: flex;

  @include notebook {
    flex-direction: column;
  }
}

.devices {
  flex-grow: 1;
}

.device {
  display: flex;
  padding: 10px;
  align-items: center;
  border-radius: 4px;
  @include transition;
  cursor: pointer;
  position: relative;
  border: 1px solid transparent;

  &:not(:first-child) {
    margin-top: 5px;
  }

  &:hover:not(&_active):not(&_disabled) {
    background-color: var(--tertiary-bg);
  }

  @include notebook {
    border: 1px solid var(--tertiary-accent);

    &_switchable {
      border-color: transparent;
    }
  }

  &_active {
    box-shadow: var(--my-device-shadow);
    border-color: transparent;
  }

  &_disabled {
    pointer-events: none;
    opacity: 0.7;
  }

  &_switchable {
    @include notebook {
      &:after {
        content: "";
        position: absolute;
        right: 10px;
        top: 50%;
        transform: translateY(-50%);
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: var(--success-text);
      }
    }
  }

  &__img-wrapper {
    flex: 0 0 44px;
    text-align: center;
    margin-right: 15px;
  }

  &__img {
    height: 45px;
    filter: var(--img-filter);
  }

  &__title {
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-bottom: 5px;
  }

  &__pro {
    text-transform: uppercase;
    background: $primary;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    padding: 3px 3px 2px;
    margin-left: 5px;
  }

  &__premium {
    text-transform: uppercase;
    background: $primary;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    padding: 3px 3px 2px;
    margin-left: 5px;
  }

  &__remove {
    color: #fa6565;
    margin-left: 5px;
  }

  &__desc {
    flex-grow: 1;
  }

  &__info {
    line-height: 21px;
  }

  &__jail {
    white-space: nowrap;
    border-radius: 2.5px;
    font-size: 12px;
    padding: 1px 3px 2px;
    line-height: 10px;
    position: relative;
    top: -1px;
    margin-left: 2px;
    background: var(--tertiary-accent);
    color: var(--text-tertiary-accent);

    &_active {
      background: var(--bg-success);
      color: #053305;
    }
  }

  &__switch {
    text-align: center;
    transition: opacity 0.3s ease, transform 0.3s ease;
    opacity: 1;
    transform: translateY(0);

    @include notebook {
      display: none;
    }
  }

  &__button {
    background: var(--bg);
    border: 1px solid lighten($primary, 20%);
    border-radius: 2px;
    color: var(--text-primary);
    font-size: 12px;
    padding: 3px 15px;
    transition: all 0.2s ease;

    &:hover {
      background: lighten($primary, 45%);
    }
  }

  &__switch-text {
    color: $text-secondary;
    font-size: 12px;
    margin-top: 2px;
    transition: color 0.2s ease;
  }

  &__mobile-indicator {
    display: none;

    @include notebook {
      display: block;
      position: absolute;
      right: 10px;
      top: 50%;
      transform: translateY(-50%);
      font-size: 12px;
      color: var(--text-secondary);
    }
  }

  &__mobile-switch {
    color: var(--success-text);
    font-weight: 500;
  }

  &__mobile-no-switch {
    color: var(--text-secondary);
  }
}

.link {
  width: 200px;
  margin-left: 50px;
  background: var(--secondary-bg);
  border-radius: 8px;
  padding: 10px;
  text-align: center;

  @include notebook {
    margin: 15px auto 0;
    width: 100%;
    max-width: 400px;
  }

  &__title {
    font-weight: 500;
    margin-bottom: 10px;
    text-align: left;
  }

  &__text {
    line-height: 15px;
    text-align: left;
  }

  &__code {
    display: block;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 5px;
    margin: 10px 0 5px;
  }

  &__qr {
    margin-top: 10px;
    margin-bottom: 15px;
  }

  &__list {
    list-style: decimal inside;
    text-align: left;

    &::v-deep {
      li {
        margin-bottom: 3px;
        line-height: 18px;
      }

      strong {
        font-weight: bold;
      }
    }
  }
}
</style>
